import {getColorByPath, CSSDimension, Integer, Show, ThemeColorPath} from 'platform/foundation';
import {cva} from 'platform/shared';

import {
  AriaRole,
  Children,
  CSSProperties,
  isValidElement,
  MouseEvent,
  ReactNode,
  useState,
} from 'react';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useComponentsTheme} from '../../../hooks/useComponentsTheme';
import {TableRowContext} from '../TableRowContext';
import {TableCellBaseProps} from '../types/TableCellBaseProps';
import {useTableContext} from '../useTableContext';
import {TableRowActions, TableRowActionsData} from './TableRowActions';

export interface TableRowProps extends TestIdProps {
  children?: ReactNode;
  actions?: TableRowActionsData;
  level?: number;
  actionsWidth?: CSSDimension | Integer;
  /**@deprecated use onRowClick or onCellClick instead */
  onClick?: () => void;
  color?: ThemeColorPath;
  hoverColor?: ThemeColorPath;
  onRowClick?: (event?: MouseEvent<HTMLTableRowElement>) => void;
  onCellClick?: (event?: MouseEvent<HTMLTableCellElement>, cellProps?: TableCellBaseProps) => void;
  role?: AriaRole;
  ariaRowIndex?: number;
}

type TableRowStyle = CSSProperties & {
  '--table-row-background-color'?: string;
  '--table-row-hover-background-color': string;
};

const tableRowVariants = cva('hover:[&_td]:bg-[var(--table-row-hover-background-color)]', {
  variants: {
    variant: {
      bordered:
        '[&_td]:border-t [&_td]:border-t-[var(--color-general-separator)] first:[&_td]:border-t-0',
      plain: '',
      stripped: 'even:[&_td]:bg-[var(--table-row-background-color)]',
    },
    hasColor: {
      true: '[&_td]:bg-[var(--table-row-background-color)]',
      false: '',
    },
    isClickable: {
      true: '[&_td]:cursor-pointer',
      false: '',
    },
  },
});

export function TableRow(props: TableRowProps) {
  const theme = useComponentsTheme();
  const context = useTableContext();
  const [isHovered, setIsHovered] = useState(false);
  const rowColor = getColorByPath(props.color, theme);
  const rowStyle: TableRowStyle = {
    '--table-row-background-color':
      rowColor ??
      (context.variant === 'stripped' ? theme.colors.palettes.neutral[10][100] : undefined),
    '--table-row-hover-background-color':
      getColorByPath(props.hoverColor, theme) ?? theme.colors.palettes.neutral[20][100],
  };

  return (
    <tr
      className={tableRowVariants({
        variant: context.variant,
        hasColor: Boolean(rowColor),
        isClickable: Boolean(props.onRowClick),
      })}
      style={rowStyle}
      onClick={props.onRowClick}
      data-testid={suffixTestId('tableRow', props)}
      role={props.role}
      aria-rowindex={props.ariaRowIndex}
    >
      {Children.map(props.children, (child) => {
        if (!isValidElement<TableCellBaseProps>(child)) {
          return;
        }

        const childProps = child.props;

        return (
          <td
            key={child.key}
            className={childProps.isCellFullHeight ? 'h-px' : undefined}
            data-testid={suffixTestId('cell', props)}
            role={childProps.role}
            aria-colindex={childProps.ariaColIndex}
            aria-selected={childProps.ariaSelected}
            onMouseEnter={() => {
              if (childProps.cellHoverDisabled) {
                return setIsHovered(false);
              }
              setIsHovered(true);
            }}
            onMouseLeave={() => {
              if (childProps.cellHoverDisabled) {
                return setIsHovered(false);
              }
              setIsHovered(false);
            }}
            onClick={(event) => {
              props.onClick?.();

              if (props.onCellClick) {
                props.onCellClick(event, childProps);
              }
            }}
          >
            <TableRowContext value={{isHovered}}>{child}</TableRowContext>
          </td>
        );
      })}
      <Show when={props.actions}>
        <TableRowActions
          actions={props.actions}
          actionsWidth={props.actionsWidth}
          data-testid={props['data-testid']}
        />
      </Show>
    </tr>
  );
}
